if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('form');
                this.form.querySelector('[name=id]').disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.submitButton = this.querySelector('[type="submit"]');

                if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

                this.hideErrors = this.dataset.hideErrors === 'true';

                // Load multi-pack cart config if available
                this.multiPackConfig = this.loadMultiPackConfig();
            }

            /**
             * Load the multi-pack cart configuration from the page
             * This config maps multi-pack variants to 1-pack with correct selling plans
             */
            loadMultiPackConfig() {
                const configEl = document.getElementById('multi-pack-cart-config');
                if (!configEl) return null;

                try {
                    return JSON.parse(configEl.textContent);
                } catch (e) {
                    console.error('[MultiPackCart] Failed to parse config:', e);
                    return null;
                }
            }

            /**
             * Check if a variant is a multi-pack and get its configuration
             * @param {string|number} variantId - The variant ID to check
             * @returns {Object|null} - Pack config or null if not a multi-pack
             */
            getMultiPackVariantConfig(variantId) {
                if (!this.multiPackConfig?.variants) return null;
                return this.multiPackConfig.variants[variantId] || null;
            }

            /**
             * Get the correct selling plan ID for a pack size
             * @param {number} packCount - The pack size (1, 2, or 4)
             * @returns {number|null} - The selling plan ID or null
             */
            getSellingPlanForPackSize(packCount) {
                if (!this.multiPackConfig?.sellingPlanMap) return null;
                const planId = this.multiPackConfig.sellingPlanMap[packCount];
                return planId && planId !== 'null' ? planId : null;
            }

            /**
             * Add multi-pack items to cart (as multiple 1-pack items)
             * @param {number} onePackVariantId - The 1-pack variant ID
             * @param {number} quantity - Number of 1-pack items to add
             * @param {number|null} sellingPlanId - The selling plan ID for subscriptions
             * @param {string} bundleType - Bundle type label (e.g., "2 Pack")
             */
            async addMultiPackToCart(onePackVariantId, quantity, sellingPlanId, bundleType) {
                const item = {
                    id: onePackVariantId,
                    quantity: quantity,
                    properties: {
                        '_bundle_type': bundleType,
                        'Bundle': bundleType  // Visible property shown in cart
                    }
                };

                // Add selling plan if subscription is selected
                if (sellingPlanId) {
                    item.selling_plan = sellingPlanId;
                }

                // Build the request body
                const body = {
                    items: [item]
                };

                // Add sections for cart drawer update
                if (this.cart) {
                    body.sections = this.cart.getSectionsToRender().map((section) => section.id);
                    body.sections_url = window.location.pathname;
                    this.cart.setActiveElement(document.activeElement);
                }

                try {
                    console.log('[MultiPackCart] Sending cart request:', JSON.stringify(body, null, 2));

                    const response = await fetch(`${routes.cart_add_url}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: JSON.stringify(body)
                    });

                    // Check if response is JSON
                    const contentType = response.headers.get('content-type');
                    let data = null;
                    let parseError = false;

                    if (!contentType || !contentType.includes('application/json')) {
                        console.warn('[MultiPackCart] Server returned non-JSON response:', response.status);
                        const text = await response.text();
                        console.warn('[MultiPackCart] Response body:', text.substring(0, 500));
                        parseError = true;
                    } else {
                        try {
                            data = await response.json();
                        } catch (jsonErr) {
                            console.warn('[MultiPackCart] Failed to parse JSON response:', jsonErr);
                            parseError = true;
                        }
                    }

                    // If we got a parse error or 422, check if item was actually added to cart
                    if (parseError || (data && data.status)) {
                        console.log('[MultiPackCart] Response error or parse issue, verifying cart state...');

                        // Fetch current cart to verify if item was added
                        const cartCheckResponse = await fetch('/cart.js', {
                            headers: { 'Content-Type': 'application/json' }
                        });
                        const cartData = await cartCheckResponse.json();

                        // Sum up all quantities for this variant (discounts may split into multiple line items)
                        // e.g., BLACKFRIDAY "Buy X get Y" makes 1 item free, creating 2 line items: 3 paid + 1 free
                        const totalQuantityInCart = cartData.items
                            .filter(item => String(item.variant_id) === String(onePackVariantId))
                            .reduce((sum, item) => sum + item.quantity, 0);

                        console.log(`[MultiPackCart] Total quantity of variant ${onePackVariantId} in cart: ${totalQuantityInCart}, expected: ${quantity}`);

                        // Check if we have at least the requested quantity (may have more due to prior cart contents)
                        const itemInCart = totalQuantityInCart >= quantity;

                        if (itemInCart) {
                            console.log('[MultiPackCart] Item was added despite error response, opening cart drawer');
                            this.error = false;

                            // Publish cart update event
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: onePackVariantId,
                                cartData: cartData,
                            });

                            // Manually refresh cart drawer sections and open it
                            if (this.cart) {
                                await this.refreshCartDrawerAndOpen();
                            } else {
                                window.location = window.routes.cart_url;
                            }
                            return;
                        } else {
                            // Item was NOT added - show actual error
                            console.error('[MultiPackCart] Item was NOT added to cart');
                            if (data && data.status) {
                                console.error('[MultiPackCart] Shopify error:', data);
                                publish(PUB_SUB_EVENTS.cartError, {
                                    source: 'product-form',
                                    productVariantId: onePackVariantId,
                                    errors: data.errors || data.description,
                                    message: data.message,
                                });
                                this.handleErrorMessage(data.description || data.message || 'Failed to add to cart');
                            } else {
                                this.handleErrorMessage('Failed to add to cart. Please try again.');
                            }
                            this.error = true;
                            return;
                        }
                    }

                    if (!this.cart) {
                        window.location = window.routes.cart_url;
                        return;
                    }

                    // Success
                    console.log('[MultiPackCart] Successfully added to cart:', data);
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'product-form',
                        productVariantId: onePackVariantId,
                        cartData: data,
                    });
                    this.error = false;

                    const quickAddModal = this.closest('quick-add-modal');
                    if (quickAddModal) {
                        document.body.addEventListener(
                            'modalClosed',
                            () => {
                                setTimeout(() => {
                                    this.cart.renderContents(data);
                                });
                            },
                            { once: true }
                        );
                        quickAddModal.hide(true);
                    } else {
                        this.cart.renderContents(data);
                    }
                } catch (e) {
                    console.error('[MultiPackCart] Error adding to cart:', e);

                    // Even on exception, check if item was added to cart
                    try {
                        const cartCheckResponse = await fetch('/cart.js', {
                            headers: { 'Content-Type': 'application/json' }
                        });
                        const cartData = await cartCheckResponse.json();

                        // Sum quantities across all line items for this variant (discounts may split items)
                        const totalQuantityInCart = cartData.items
                            .filter(item => String(item.variant_id) === String(onePackVariantId))
                            .reduce((sum, item) => sum + item.quantity, 0);
                        const itemInCart = totalQuantityInCart >= quantity;

                        if (itemInCart) {
                            console.log('[MultiPackCart] Item found in cart despite error, recovering...');
                            this.error = false;
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: onePackVariantId,
                                cartData: cartData,
                            });
                            if (this.cart) {
                                await this.refreshCartDrawerAndOpen();
                            }
                            return;
                        }
                    } catch (checkError) {
                        console.error('[MultiPackCart] Could not verify cart state:', checkError);
                    }

                    this.handleErrorMessage('Failed to add to cart. Please try again.');
                    this.error = true;
                } finally {
                    this.submitButton.classList.remove('loading');
                    if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                    if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                    const spinner = this.querySelector('.loading__spinner');
                    if (spinner) spinner.classList.add('hidden');
                }
            }

            /**
             * Manually refresh cart drawer sections and open the drawer
             * Used as fallback when section rendering fails in the cart add response
             */
            async refreshCartDrawerAndOpen() {
                if (!this.cart) return;

                try {
                    // Get section IDs to refresh
                    const sections = this.cart.getSectionsToRender().map((section) => section.id);

                    // Fetch fresh section content
                    const sectionUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
                    const response = await fetch(sectionUrl);

                    if (!response.ok) {
                        console.warn('[MultiPackCart] Could not fetch sections, opening cart drawer anyway');
                        // Try to open cart drawer with page refresh as fallback
                        if (typeof this.cart.open === 'function') {
                            this.cart.open();
                        }
                        return;
                    }

                    const sectionsData = await response.json();

                    // Create a response object similar to what cart add returns
                    const cartResponse = {
                        sections: sectionsData
                    };

                    console.log('[MultiPackCart] Rendering cart drawer with fresh sections');
                    this.cart.renderContents(cartResponse);
                } catch (e) {
                    console.error('[MultiPackCart] Error refreshing cart drawer:', e);
                    // As a last resort, just try to open the cart drawer
                    if (typeof this.cart.open === 'function') {
                        this.cart.open();
                    }
                }
            }

            onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

                this.handleErrorMessage();

                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.classList.add('loading');
                const spinner = this.querySelector('.loading__spinner');
                if (spinner) spinner.classList.remove('hidden');

                // Pack-size selector (custom UI) drives multi-pack behavior now.
                // The 1-Pack is the only real variant; 2/4-Pack are expressed as
                // selling-plan + quantity multiplier at cart-add time.
                const packSelectorActive = typeof window._currentPackCount !== 'undefined' && this.multiPackConfig?.onePackVariantId;

                if (packSelectorActive) {
                    const packCount = parseInt(window._currentPackCount, 10) || 1;
                    const onePackId = this.multiPackConfig.onePackVariantId;
                    const sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
                    const hasSubscription = sellingPlanInput && sellingPlanInput.value && sellingPlanInput.value !== '';

                    let sellingPlanId = null;
                    if (hasSubscription) {
                        sellingPlanId = this.getSellingPlanForPackSize(packCount);
                        if (!sellingPlanId) {
                            sellingPlanId = sellingPlanInput.value;
                        }
                    }

                    if (packCount > 1) {
                        this.addMultiPackToCart(onePackId, packCount, sellingPlanId, `${packCount} Pack`);
                        return;
                    }

                    // 1-Pack: ensure the form submits the correct selling plan even if
                    // Stay AI's widget defaulted to a different plan for this variant.
                    if (hasSubscription && sellingPlanId && sellingPlanInput.value !== String(sellingPlanId)) {
                        sellingPlanInput.value = String(sellingPlanId);
                    }
                }

                // Normal flow for 1-pack or products without multi-pack config
                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }
                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);

                            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                            if (!soldOutMessage) return;
                            this.submitButton.setAttribute('aria-disabled', true);
                            this.submitButton.querySelector('span').classList.add('hidden');
                            soldOutMessage.classList.remove('hidden');
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        if (!this.error)
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                cartData: response,
                            });
                        this.error = false;
                        const quickAddModal = this.closest('quick-add-modal');
                        if (quickAddModal) {
                            document.body.addEventListener(
                                'modalClosed',
                                () => {
                                    setTimeout(() => {
                                        this.cart.renderContents(response);
                                    });
                                },
                                { once: true }
                            );
                            quickAddModal.hide(true);
                        } else {
                            this.cart.renderContents(response);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton.classList.remove('loading');
                        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                        const spinner = this.querySelector('.loading__spinner');
                        if (spinner) spinner.classList.add('hidden');
                    });
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) return;
                this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                }
            }
        }
    );
}
